<template>
  <div class="pdf-viewer" ref="viewer">
    <!-- Toolbar -->
    <div class="pdf-toolbar">
      <span class="pdf-toolbar-page">
        <i class="fas fa-file-pdf"></i> Página {{ currentPage }} / {{ pages.length }}
      </span>

      <div class="  sticky">
        <!-- <div class="pdf-toolbar-left">
          <span class="pdf-toolbar-page">
            Página <b>{{ currentPage }}</b> de <b>{{ pages.length }}</b>
          </span>
        </div> -->

        <div class="pdf-toolbar-right">
          <vs-tooltip>
            <vs-button
              success
              class="toolbar-btn"
              size="lg"
              @click="downloadPdf"
            >
              <i class="fas fa-download"></i>
            </vs-button>
            <template #tooltip>Descargar PDF</template>
          </vs-tooltip>

          <vs-tooltip>
            <vs-button class="toolbar-btn" size="lg" @click="toggleFullscreen">
              <i
                class="fas"
                :class="isFullscreen ? 'fa-compress' : 'fa-expand'"
              ></i>
            </vs-button>
            <template #tooltip>Pantalla completa</template>
          </vs-tooltip>
        </div>
      </div>
    </div>

    <!-- Pages -->
    <div
      v-for="(_, index) in pages"
      :key="index"
      class="pdf-page"
      :ref="'wrapper' + index"
    >
      <canvas :ref="'page' + index"></canvas>

      <!-- Footer de página (impreso) -->
      <div class="pdf-page-footer">
        Página {{ index + 1 }} de {{ pages.length }}
      </div>
    </div>
  </div>
</template>

<script>
const pdfjsLib = require("pdfjs-dist/legacy/build/pdf.js");
const pdfjsWorker = require("pdfjs-dist/legacy/build/pdf.worker.entry.js");

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

export default {
  name: "PDFViewer",
  props: {
    pdfUrl: { required: true },
  },
  data() {
    return {
      pages: [],
      pdf: null,
      currentPage: 1,
      observer: null,
      isFullscreen: false,
    };
  },
  mounted() {
    if (this.pdfUrl) this.loadPdf(this.pdfUrl);
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
  },
  watch: {
    pdfUrl(val) {
      if (val) this.loadPdf(val);
    },
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
  },
  methods: {
    toggleFullscreen() {
      const el = this.$refs.viewer;

      if (!document.fullscreenElement) {
        el.requestFullscreen?.() ||
          el.webkitRequestFullscreen?.() ||
          el.msRequestFullscreen?.();
      } else {
        document.exitFullscreen?.() ||
          document.webkitExitFullscreen?.() ||
          document.msExitFullscreen?.();
      }
    },
    onFullscreenChange() {
      this.isFullscreen = !!document.fullscreenElement;

      // Re-render al cambiar tamaño
      this.$nextTick(() => {
        this.renderPages();
      });
    },
    async loadPdf(url) {
      const loadingTask = pdfjsLib.getDocument(url);
      this.pdf = await loadingTask.promise;
      this.pages = Array.from({ length: this.pdf.numPages });

      this.$nextTick(async () => {
        await this.renderPages();
        this.initObserver();
      });
    },

    async renderPages() {
      const containerWidth = this.$refs.viewer.clientWidth - 32;
      const dpr = window.devicePixelRatio || 1;

      for (let i = 1; i <= this.pdf.numPages; i++) {
        const page = await this.pdf.getPage(i);

        // Viewport base
        const viewport = page.getViewport({ scale: 1 });

        // Escala según ancho del contenedor
        const scale = containerWidth / viewport.width;

        // Viewport visual
        const scaledViewport = page.getViewport({ scale });

        // Viewport REAL (alta resolución)
        const renderViewport = page.getViewport({
          scale: scale * dpr,
        });

        const canvas = this.$refs["page" + (i - 1)][0];
        const ctx = canvas.getContext("2d");

        // Tamaño REAL del canvas (alta resolución)
        canvas.width = Math.floor(renderViewport.width);
        canvas.height = Math.floor(renderViewport.height);

        // Tamaño VISUAL (CSS)
        canvas.style.width = `${scaledViewport.width}px`;
        canvas.style.height = `${scaledViewport.height}px`;

        // Ajuste del contexto
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        await page.render({
          canvasContext: ctx,
          viewport: scaledViewport,
        }).promise;
      }
    },
    initObserver() {
      const wrappers = this.pages.map((_, i) => this.$refs["wrapper" + i][0]);

      this.observer = new IntersectionObserver(
        (entries) => {
          let max = 0;
          let page = this.currentPage;

          entries.forEach((e) => {
            if (e.intersectionRatio > max) {
              max = e.intersectionRatio;
              page = wrappers.indexOf(e.target) + 1;
            }
          });

          this.currentPage = page;
        },
        {
          root: null,
          threshold: [0.3, 0.6, 0.9],
        }
      );

      wrappers.forEach((w) => this.observer.observe(w));
    },

    downloadPdf() {
      const link = document.createElement("a");
      link.href = this.pdfUrl;
      link.download = "documento.pdf";
      link.click();
    },
  },
};
</script>

<style scoped>

.pdf-viewer:fullscreen {
  background: #e6e6e6;
  height: 100vh;
  width: 100vw;
}

.pdf-viewer:-webkit-full-screen {
  background: #e6e6e6;
  height: 100vh;
  width: 100vw;
}
/* CONTENEDOR GENERAL */
.pdf-viewer {
  background: var(--dark-bg);
  height: 100%;
  overflow-y: auto;
}

/* TOOLBAR SUPERIOR */
.pdf-toolbar {
  position: sticky;
  top: 0;
  z-index: 50;

  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 10px 16px;
  /* background: rgba(255, 255, 255, 0.95); */
  background-color: rgba(20, 84, 247, 0.212);
  backdrop-filter: blur(6px);

  border-bottom: 1px solid var(--primary)
  /* box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); */
}

/* IZQUIERDA */
.pdf-toolbar-left {
  display: flex;
  align-items: center;
}

.pdf-toolbar-page {
  font-size: 14px;
  color: white;
  white-space: nowrap;
  background-color: var(--primary);
  padding: 4px 20px;
  border-radius: 500px !important;
}

/* DERECHA */
.pdf-toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

/* Botones */
.toolbar-btn {
  width: 42px !important;
  height: 42px !important;
  min-width: 42px !important;
  min-height: 42px !important;
  padding: 0 !important;

  display: flex !important;
  align-items: center !important;
  justify-content: center !important;

  border-radius: 8px;
}

/* Responsive */
@media (max-width: 480px) {
  .pdf-toolbar {
    padding: 8px 12px;
  }

  .pdf-toolbar-page {
    font-size: 12px;
  }
}

/* PÁGINA */
.pdf-page {
  background: white;
  margin: 24px auto;
  padding: 16px 16px 40px;
  max-width: 100%;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
  position: relative;
}

/* FOOTER IMPRESO */
.pdf-page-footer {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
  text-align: center;
  font-size: 12px;
  color: #555;
  font-family: Arial, sans-serif;
}

/* CANVAS RESPONSIVE */
canvas {
  display: block;
  width: 100%;
  height: auto;
}
</style>
